import { useRef, useState } from 'react';
import {
    previewSecurityResolutionAdd,
    searchWatchlistSecurityDiscovery,
} from '../../../utils/ApiCall';
import { parseCloudRunError } from '../../msiDriftReviewHelpers';
import {
    identifierTypeLabel,
    pickFromDiscoveryResult,
    pickFromIdentifierOption,
    unresolvedPickFromPreview,
} from './pendingAdditions';
import {
    SEARCH_RESULT_LIMIT,
    isValidDiscoveryResponse,
    orderSearchResults,
} from '../securityAdditionContract';

// The Add Security dialog's search (WatchlistTickerBuilder) and identifier
// lookup (WatchlistAddSecurityDialog), driven from input handlers: timers are
// started and cleared by the handlers, never by an effect.
export const SEARCH_DEBOUNCE_MS = 200;
export const MIN_QUERY_CHARACTERS = 2;
export const MAX_QUERY_CHARACTERS = 128;
// The longest identifier the add flow stages (MAX_ADD_TICKER_LENGTH).
const MAX_IDENTIFIER_CHARACTERS = 64;
const SEARCH_UNAVAILABLE = 'Security search is temporarily unavailable.';
const LOOKUP_FAILED = 'Could not resolve this security.';

// The Watchlist's search. Tests pass a stand-in with the same request and
// response contracts as `searchApi`.
export const WATCHLIST_SEARCH_API = Object.freeze({
    searchWatchlistSecurityDiscovery,
    previewSecurityResolutionAdd,
});

export const IDLE_SEARCH_STATE = Object.freeze({
    input: '',
    query: '',
    // idle | minimum | debouncing | loading | success | empty | error
    status: 'idle',
    // discovery: ticker and company search; identifier: listings of one ISIN
    // or ticker looked up exactly
    mode: 'discovery',
    candidates: Object.freeze([]),
    // { type, value } of the looked-up identifier in identifier mode
    identifier: null,
    // The "Add as unresolved…" offer: { label, lookup: true } before the
    // lookup, { label, pick } once the lookup says Apply can resolve it.
    unresolved: null,
    // The server's { max_securities, current_security_count }, kept across queries
    capacity: null,
    message: '',
});

export function normalizeQuery(value) {
    return String(value || '').normalize('NFC').trim();
}

/** Two letters, nine letters or digits and a check digit, spaces and dashes aside. */
export function isIsinShaped(query) {
    return /^[A-Z]{2}[A-Z0-9]{9}[0-9]$/.test(String(query || '').replace(/[\s-]/g, '').toUpperCase());
}

/** One word short enough to stage as a ticker (company names have spaces). */
export function isIdentifierLike(query) {
    const value = normalizeQuery(query);
    return Boolean(value)
        && Array.from(value).length <= MAX_IDENTIFIER_CHARACTERS
        && !/\s/.test(value)
        && /[\p{L}\p{N}]/u.test(value);
}

// The server searches at least two characters, one of them a letter or digit.
function isSearchable(query) {
    return Array.from(query).length >= MIN_QUERY_CHARACTERS && /[\p{L}\p{N}]/u.test(query);
}

// The dialog's identifier lookup inputs: the identifier as typed, and the
// ticker to show only when the identifier is a ticker.
export function identifierLookupRequest(query) {
    const identifierValue = normalizeQuery(query);
    const identifierType = isIsinShaped(identifierValue) ? 'isin' : 'ticker';
    return {
        identifierType,
        identifierValue,
        ticker: identifierType === 'ticker' ? identifierValue : undefined,
        requestedCountry: undefined,
    };
}

function unresolvedLabel(type, value) {
    return type === 'ticker' ? `Add "${value}" as unresolved…` : `Add ${identifierTypeLabel(type)} ${value} as unresolved…`;
}

function candidateFromDiscoveryResult(result) {
    return Object.freeze({
        key: result.option_token,
        ticker: result.ticker,
        companyName: result.display_name || '',
        country: result.country,
        exchange: result.exchange || result.mic || '',
        exactTicker: result.match_kind === 'exact_ticker',
        inWatchlist: result.already_active,
        unavailableReason: '',
        pick: pickFromDiscoveryResult(result),
    });
}

function candidateFromIdentifierOption(preview, option, index) {
    const inWatchlist = option.status === 'already_active';
    const pick = pickFromIdentifierOption(preview, option);
    return Object.freeze({
        key: typeof option.option_token === 'string' && option.option_token
            ? option.option_token
            : `option-${index}`,
        ticker: typeof option.ticker === 'string' ? option.ticker : '',
        companyName: '',
        country: typeof option.country === 'string' ? option.country : '',
        exchange: '',
        exactTicker: false,
        inWatchlist,
        unavailableReason: pick || inWatchlist ? '' : (option.reason || 'This listing cannot be added.'),
        pick,
    });
}

function discoveryOutcome(query, response) {
    if (response?.success === false) {
        return { status: 'error', message: response?.message || SEARCH_UNAVAILABLE };
    }
    if (!isValidDiscoveryResponse(response)) {
        return { status: 'error', message: 'Security search returned an invalid response. Please retry.' };
    }
    // US listings first, then the rest, each by ticker. An identifier lookup
    // (one security's listings) keeps MSI's order.
    const candidates = orderSearchResults(response.results).map(candidateFromDiscoveryResult);
    // No results at all: offer to add the text itself, as the dialog's "Add
    // by identifier" does.
    const unresolved = isIdentifierLike(query) && candidates.length === 0
        ? { label: unresolvedLabel('ticker', query), lookup: true }
        : null;
    return {
        status: candidates.length ? 'success' : 'empty',
        candidates,
        unresolved,
        capacity: response.capacity,
    };
}

function identifierOutcome(request, response) {
    if (response?.success === false) {
        return {
            status: 'error',
            message: parseCloudRunError(response?.message || response).message || LOOKUP_FAILED,
        };
    }
    if (typeof response?.preview_token !== 'string' || !response.preview_token) {
        return { status: 'error', message: LOOKUP_FAILED };
    }
    const options = (Array.isArray(response.options) ? response.options : [])
        .filter((option) => option && typeof option === 'object');
    const candidates = options.map((option, index) => candidateFromIdentifierOption(response, option, index));
    const identifier = {
        type: String(response.identifier_type || request.identifierType).toLowerCase(),
        value: response.identifier_value_norm || request.identifierValue,
    };
    // Offered only when the lookup found no listing at all.
    const unresolvedPick = candidates.length ? null : unresolvedPickFromPreview(response);
    const addable = candidates.some((candidate) => candidate.pick) || Boolean(unresolvedPick);
    return {
        status: candidates.length ? 'success' : 'empty',
        candidates,
        identifier,
        unresolved: unresolvedPick
            ? { label: unresolvedLabel(identifier.type, identifier.value), pick: unresolvedPick }
            : null,
        message: addable ? '' : (response.message || 'None of these listings can be added.'),
    };
}

async function performRequest(request, api) {
    try {
        if (request.kind === 'lookup' || isIsinShaped(request.query)) {
            const lookup = identifierLookupRequest(request.query);
            return identifierOutcome(lookup, await api.previewSecurityResolutionAdd(lookup));
        }
        const response = await api.searchWatchlistSecurityDiscovery({
            query: request.query,
            country: undefined,
            limit: SEARCH_RESULT_LIMIT,
        });
        return discoveryOutcome(request.query, response);
    } catch (_error) {
        return { status: 'error', message: SEARCH_UNAVAILABLE };
    }
}

// All search state transitions. Every handler that starts new work moves the
// generation on, so an older response can never overwrite a newer one.
function createSearchController(setState, readOptions, initialState) {
    let current = initialState;
    let generation = 0;
    let timerId = null;
    let scheduledStart = null;
    let inFlight = null;
    let lastRequest = null;

    const commit = (next) => {
        current = next;
        setState(next);
        return next;
    };
    const stopTimer = () => {
        if (timerId !== null) window.clearTimeout(timerId);
        timerId = null;
        scheduledStart = null;
    };
    const supersede = () => {
        stopTimer();
        inFlight = null;
        generation += 1;
        return generation;
    };
    // Resolves with the state the response produced, or null when newer
    // input superseded it.
    const execute = (requestGeneration, request) => {
        lastRequest = request;
        const identifierMode = request.kind === 'lookup' || isIsinShaped(request.query);
        commit({
            ...current,
            status: 'loading',
            mode: identifierMode ? 'identifier' : 'discovery',
            candidates: IDLE_SEARCH_STATE.candidates,
            identifier: null,
            unresolved: null,
            message: '',
        });
        const promise = performRequest(request, readOptions().searchApi).then((outcome) => {
            if (requestGeneration !== generation) return null;
            inFlight = null;
            if (outcome.capacity) readOptions().onCapacity?.(outcome.capacity);
            return commit({ ...current, ...outcome });
        });
        inFlight = { promise };
        return promise;
    };

    const setInput = (value) => {
        const requestGeneration = supersede();
        const input = String(value ?? '');
        const query = normalizeQuery(input);
        const next = {
            ...IDLE_SEARCH_STATE,
            input,
            query,
            mode: isIsinShaped(query) ? 'identifier' : 'discovery',
            capacity: current.capacity,
        };
        if (!query) {
            commit(next);
            return;
        }
        if (!isSearchable(query)) {
            commit({ ...next, status: 'minimum' });
            return;
        }
        commit({ ...next, status: 'debouncing' });
        const start = () => {
            stopTimer();
            return execute(requestGeneration, { kind: 'search', query });
        };
        scheduledStart = start;
        timerId = window.setTimeout(start, readOptions().debounceMs);
    };

    return Object.freeze({
        setInput,
        // Start a waiting search now. Resolves with the settled state, or null
        // when newer input superseded it.
        flush: () => {
            if (scheduledStart) return scheduledStart();
            if (inFlight) return inFlight.promise;
            return Promise.resolve(current);
        },
        retry: () => {
            if (!lastRequest) return Promise.resolve(null);
            const request = lastRequest;
            return execute(supersede(), request);
        },
        // Look the typed text up as an identifier, as the dialog's "Add by
        // identifier" does. Resolves with { type: 'unresolved', pick } when MSI
        // has no listing but Apply can look it up, { type: 'choices' } when it
        // shows listings to choose from, { type: 'error', message }, or null
        // when superseded.
        lookupIdentifier: async () => {
            const { query } = current;
            if (!isIdentifierLike(query) && !isIsinShaped(query)) return null;
            const settled = await execute(supersede(), { kind: 'lookup', query });
            if (!settled) return null;
            if (settled.status === 'error') return { type: 'error', message: settled.message };
            if (!settled.candidates.length && settled.unresolved?.pick) {
                return { type: 'unresolved', pick: settled.unresolved.pick };
            }
            return { type: 'choices' };
        },
        // Clear the query for the next entry; the server capacity is kept.
        reset: () => {
            supersede();
            commit({ ...IDLE_SEARCH_STATE, capacity: current.capacity });
        },
        // Stop timers and ignore responses still on their way (closing, unmount).
        cancel: () => {
            supersede();
        },
    });
}

/**
 * Search-as-you-type for the "+ Add security" row: a debounced discovery
 * search (WatchlistTickerBuilder), or the identifier lookup
 * (previewSecurityResolutionAdd) for an ISIN-shaped query and for "Add as
 * unresolved…". Returns the state plus stable handlers: setInput, flush,
 * retry, lookupIdentifier, reset and cancel.
 *
 * `initialQuery` only fills the input; call setInput(initialQuery) to search
 * it. `onCapacity(capacity)` receives each server-reported capacity.
 * `searchApi` ({ searchWatchlistSecurityDiscovery,
 * previewSecurityResolutionAdd }) replaces the backend calls.
 */
export default function useAddSecuritySearch({
    initialQuery = '',
    debounceMs = SEARCH_DEBOUNCE_MS,
    onCapacity = null,
    searchApi = WATCHLIST_SEARCH_API,
} = {}) {
    const [state, setState] = useState(() => ({ ...IDLE_SEARCH_STATE, input: String(initialQuery || '') }));
    const optionsRef = useRef(null);
    optionsRef.current = { debounceMs, onCapacity, searchApi };
    const [controller] = useState(() => createSearchController(setState, () => optionsRef.current, state));
    return { ...state, ...controller };
}
